import { gql } from "@apollo/client";
import { ApiService } from "./ApiService";
import { OpResult } from "@sdflc/api-helpers";
import { Pagination, SortItem } from "./commonApiTypes";

// GraphQL queries
const APP_PLAN_LIST_QUERY = gql`
  query AppPlanList($filter: AppPlanFilter, $params: PaginationAndSorting) {
    appPlanList(filter: $filter, params: $params) {
      code
      data {
        id
        planCode
        planName
        planDescription
        isPlanFeatured
        status
        prices {
          id
          currency
          recurrence
          price
        }
        planFeatures {
          featureValue
          feature {
            id
            featureCode
            featureIcon
            featureName
            featureDescription
            featureValueType
            featureDefaultValue
            status
          }
        }
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const APP_PLAN_GET_QUERY = gql`
  query AppPlanGet($id: ID) {
    appPlanGet(id: $id) {
      code
      data {
        id
        planCode
        planName
        planDescription
        isPlanFeatured
        status
        prices {
          id
          currency
          recurrence
          price
        }
        planFeatures {
          featureValue
          feature {
            id
            featureCode
            featureIcon
            featureName
            featureDescription
            featureValueType
            featureDefaultValue
            status
          }
        }
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const APP_PLAN_GET_MANY_QUERY = gql`
  query AppPlanGetMany($ids: [ID]) {
    appPlanGetMany(ids: $ids) {
      code
      data {
        id
        planCode
        planName
        planDescription
        isPlanFeatured
        status
        prices {
          id
          currency
          recurrence
          price
        }
        planFeatures {
          featureValue
          feature {
            id
            featureCode
            featureIcon
            featureName
            featureDescription
            featureValueType
            featureDefaultValue
            status
          }
        }
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

export interface AppPlanPrice {
  id: string;
  currency?: string;
  recurrence?: string;
  price?: number;
  status?: number;
}

export interface AppFeature {
  id: string;
  featureCode?: string;
  featureIcon?: string | null;
  featureName?: string | null;
  featureDescription?: string | null;
  featureValue?: string | null;
  featureValueType?: string | null;
  status?: number | null;
}

export interface AppPlanFeature {
  featureValue?: string;
  feature?: AppFeature;
}

export interface AppPlan {
  id: string;
  planCode?: string;
  planName?: string;
  planDescription?: string;
  isPlanFeatured?: boolean;
  status?: number;
  prices?: AppPlanPrice[];
  planFeatures?: AppPlanFeature[];
}

export interface AppPlanFilter {
  id?: string[] | null | undefined;
  planCode?: string[] | null | undefined;
  isFeatured?: boolean | null | undefined;
  status?: number[] | null | undefined;
  searchKeyword?: string | null | undefined;
}

/**
 * AppPlan service that handles all app plan-related API calls
 * Supports read operations: list, get, getMany
 */
export class AppPlanService {
  /**
   * Get a list of app plans with optional filtering, pagination, and sorting
   */
  static async list(
    filter?: AppPlanFilter,
    pagination?: Pagination,
    sortBy?: SortItem[]
  ): Promise<OpResult> {
    const result = await ApiService.executeQuery(APP_PLAN_LIST_QUERY, {
      filter: filter || {},
      params: {
        pagination: pagination || undefined,
        sortBy: sortBy || undefined,
      },
    });
    return result;
  }

  /**
   * Get a single app plan by ID
   */
  static async get(id: string): Promise<OpResult> {
    const result = await ApiService.executeQuery(APP_PLAN_GET_QUERY, {
      id: id,
    });
    return result;
  }

  /**
   * Get multiple app plans by IDs (preserves order)
   */
  static async getMany(ids: string[]): Promise<OpResult> {
    const result = await ApiService.executeQuery(APP_PLAN_GET_MANY_QUERY, {
      ids: ids,
    });
    return result;
  }
}
